import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import moment from 'moment';
import { useDiary, formatTime, ACTIONS } from './DiaryContext';
import { buildMonthGrid, shiftMonth, monthKeyOf, monthLandingDay, summariseDay, summariseMonth, tint, WEEKDAY_LABELS } from './monthData';
import './MonthView.css';

// In-memory cache, per stylist + grid range, for the life of the page.
// Shown instantly when you come back to a month; refreshed behind it when
// older than CACHE_FRESH_MS, or when the diary says something changed.
const monthCache = new Map();
const CACHE_FRESH_MS = 60 * 1000;
export const MONTH_REFRESH_EVENT = 'dv2:month-refresh';

/** Throw away cached months (something was booked, moved or cancelled). */
export function refreshMonthView() {
  monthCache.clear();
  try { window.dispatchEvent(new CustomEvent(MONTH_REFRESH_EVENT)); } catch (e) { /* ignore */ }
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function pct(u) {
  return u == null ? null : Math.round(u * 100);
}

/**
 * MonthView - one stylist's month, Apple-calendar style.
 * Opened from a staff header's "View Month". Data is the same diary call the
 * week view makes, for the whole calendar grid, via onLoadMonth.
 */
function MonthView() {
  const { state, dispatch, onBackToDay, onDayTap, onEventClick, onLoadMonth, onMonthChange } = useDiary();
  const { selectedStaffId, staffById, selectedDate } = state;
  const staff = staffById[selectedStaffId];
  const staffId = selectedStaffId;

  const [anchor, setAnchor] = useState(() => moment(selectedDate || undefined).startOf('month').format('YYYY-MM-DD'));
  // Follow the diary's date picker when it lands in another month.
  useEffect(() => {
    if (!selectedDate) return;
    setAnchor(prev => (monthKeyOf(prev) === monthKeyOf(selectedDate) ? prev : moment(selectedDate).startOf('month').format('YYYY-MM-DD')));
  }, [selectedDate]);

  const todayKey = moment().format('YYYY-MM-DD');
  const grid = useMemo(() => buildMonthGrid(anchor, todayKey), [anchor, todayKey]);
  // Another month: the diary's date moves with it, so the date picker at the
  // top shows the same month and the day view opens there afterwards.
  const goToMonth = (date) => {
    setAnchor(moment(date).startOf('month').format('YYYY-MM-DD'));
    if (onMonthChange) onMonthChange(monthLandingDay(date, todayKey));
  };
  const cacheKey = `${staffId}|${grid.startDate}|${grid.endDate}`;

  const [data, setData] = useState(() => monthCache.get(cacheKey) || null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const reqRef = useRef(0);

  useEffect(() => {
    const onRefresh = () => setTick(t => t + 1);
    window.addEventListener(MONTH_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(MONTH_REFRESH_EVENT, onRefresh);
  }, []);

  useEffect(() => {
    if (!staffId || !onLoadMonth) return;
    const cached = monthCache.get(cacheKey);
    setData(cached || null);
    setFailed(false);
    if (cached && Date.now() - cached.at < CACHE_FRESH_MS) {
      setLoading(false);
      return;
    }
    const req = ++reqRef.current;
    setLoading(true);
    Promise.resolve(onLoadMonth(staff || { id: staffId }, grid.startDate, grid.endDate))
      .then((res) => {
        if (req !== reqRef.current) return;
        const entry = { weekData: (res && res.weekData) || {}, weekStaffInfo: (res && res.weekStaffInfo) || {}, at: Date.now() };
        monthCache.set(cacheKey, entry);
        setData(entry);
        setLoading(false);
      })
      .catch(() => {
        if (req !== reqRef.current) return;
        setLoading(false);
        setFailed(true);
      });
    // staff object changes on every day reload; the id is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId, cacheKey, tick, onLoadMonth]);

  const summaries = useMemo(() => {
    if (!data) return null;
    const out = {};
    grid.days.forEach((d) => {
      out[d.key] = summariseDay(data.weekData[d.key] || [], data.weekStaffInfo[d.key] || {});
    });
    return out;
  }, [data, grid]);

  const totals = useMemo(() => (summaries ? summariseMonth(grid, summaries) : null), [grid, summaries]);

  const handleBack = () => {
    if (onBackToDay) onBackToDay();
    else {
      dispatch({ type: ACTIONS.SET_VIEW_MODE, payload: 'day' });
      dispatch({ type: ACTIONS.SET_SELECTED_STAFF, payload: null });
    }
  };

  const openDay = useCallback((dayKey) => {
    if (onDayTap) onDayTap(dayKey, staffId);
  }, [onDayTap, staffId]);

  const openItem = useCallback((e, item, dayKey) => {
    e.stopPropagation();
    if (item.event && onEventClick) onEventClick({ ...item.event, date: item.event.date || dayKey });
    else openDay(dayKey);
  }, [onEventClick, openDay]);

  const [imgError, setImgError] = useState(false);
  const isCurrentMonth = grid.monthKey === monthKeyOf(todayKey);
  const showSkeleton = !summaries;

  return (
    <div className="dv2-viewport dv2-month">
      <div className="dv2-month-header">
        <button type="button" className="dv2-month-back" onClick={handleBack} aria-label="Back to diary">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Diary</span>
        </button>
        <div className="dv2-month-who">
          {staff && staff.avatar && !imgError
            ? <img className="dv2-month-avatar" src={staff.avatar} alt="" onError={() => setImgError(true)} />
            : <span className="dv2-month-avatar dv2-month-avatar--initials">{getInitials(staff && staff.name)}</span>}
          <div className="dv2-month-who-text">
            <div className="dv2-month-title">{grid.title}</div>
            <div className="dv2-month-sub">
              {staff ? staff.name : ''}
              {totals && (
                <span className="dv2-month-totals">
                  {' · '}{totals.bookings} booking{totals.bookings === 1 ? '' : 's'}
                  {totals.utilisation != null && ` · ${pct(totals.utilisation)}% booked`}
                </span>
              )}
              {loading && summaries && <span className="dv2-month-refreshing" aria-label="Refreshing" />}
            </div>
          </div>
        </div>
        <div className="dv2-month-nav">
          <button type="button" className="dv2-month-today" onClick={() => goToMonth(todayKey)} disabled={isCurrentMonth}>Today</button>
          <button type="button" className="dv2-month-arrow" onClick={() => goToMonth(shiftMonth(anchor, -1))} aria-label="Previous month">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <button type="button" className="dv2-month-arrow" onClick={() => goToMonth(shiftMonth(anchor, 1))} aria-label="Next month">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </div>

      {failed && !summaries && (
        <div className="dv2-month-error">
          Couldn't load this month. <button type="button" onClick={() => setTick(t => t + 1)}>Try again</button>
        </div>
      )}

      {/* Desktop / tablet: the grid */}
      <div className="dv2-month-grid" role="grid" aria-label={grid.title}>
        <div className="dv2-month-weekdays" role="row">
          {WEEKDAY_LABELS.map(l => <div key={l} className="dv2-month-weekday" role="columnheader">{l}</div>)}
        </div>
        {grid.weeks.map(week => (
          <div className="dv2-month-week" role="row" key={week[0].key}>
            {week.map(day => (
              <MonthCell
                key={day.key}
                day={day}
                summary={summaries ? summaries[day.key] : null}
                skeleton={showSkeleton}
                onOpenDay={openDay}
                onOpenItem={openItem}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Phone: a list of the month's days */}
      <div className="dv2-month-list">
        {grid.days.filter(d => d.inMonth).map(day => (
          <MonthListDay
            key={day.key}
            day={day}
            summary={summaries ? summaries[day.key] : null}
            skeleton={showSkeleton}
            onOpenDay={openDay}
            onOpenItem={openItem}
          />
        ))}
      </div>
    </div>
  );
}

function UtilBar({ summary }) {
  if (!summary || summary.utilisation == null) return null;
  const p = pct(summary.utilisation);
  return (
    <div className="dv2-month-util" title={`${p}% booked`} aria-label={`${p}% booked`}>
      <div className={`dv2-month-util-fill${p >= 85 ? ' dv2-month-util-fill--full' : ''}`} style={{ width: `${p}%` }} />
    </div>
  );
}

function ItemRow({ item, dayKey, onOpenItem }) {
  if (item.kind === 'booking') {
    const label = [item.clientFirstName, item.serviceName].filter(Boolean).join(' · ') || 'Booking';
    return (
      <button
        type="button"
        className="dv2-month-item dv2-month-item--booking"
        style={{ background: tint(item.colour || '#D6D5EC', 0.35), boxShadow: `inset 3px 0 0 ${item.colour || '#D6D5EC'}` }}
        onClick={(e) => onOpenItem(e, item, dayKey)}
        title={`${formatTime(item.startMinutes)} ${label}`}
      >
        <span className="dv2-month-item-time">{formatTime(item.startMinutes)}</span>
        <span className="dv2-month-item-text">{label}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className="dv2-month-item dv2-month-item--event"
      onClick={(e) => onOpenItem(e, item, dayKey)}
      title={`${formatTime(item.startMinutes)} to ${formatTime(item.endMinutes)} ${item.title}`}
    >
      <span className="dv2-month-item-time">{formatTime(item.startMinutes)}</span>
      <span className="dv2-month-item-text">{item.emoji ? `${item.emoji} ` : ''}{item.title}</span>
    </button>
  );
}

function MonthCell({ day, summary, skeleton, onOpenDay, onOpenItem }) {
  const cls = [
    'dv2-month-cell',
    !day.inMonth && 'dv2-month-cell--outside',
    day.isToday && 'dv2-month-cell--today',
    summary && summary.isOff && 'dv2-month-cell--off',
    summary && summary.isHoliday && 'dv2-month-cell--holiday',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      role="gridcell"
      tabIndex={0}
      onClick={() => onOpenDay(day.key)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDay(day.key); } }}
      aria-label={moment(day.key).format('dddd D MMMM')}
    >
      <div className="dv2-month-cell-top">
        <span className="dv2-month-date">{day.dayNum}</span>
        {summary && summary.isOff && <span className="dv2-month-off">Off</span>}
        {summary && summary.isHoliday && <span className="dv2-month-leave">{summary.holidayLabel}</span>}
      </div>
      {skeleton ? (
        <div className="dv2-month-skel">
          <span className="dv2-month-skel-bar" />
          <span className="dv2-month-skel-line" />
          <span className="dv2-month-skel-line dv2-month-skel-line--short" />
        </div>
      ) : summary ? (
        <>
          <UtilBar summary={summary} />
          <div className="dv2-month-items">
            {summary.items.map(item => <ItemRow key={item.kind + item.id} item={item} dayKey={day.key} onOpenItem={onOpenItem} />)}
            {summary.moreCount > 0 && <span className="dv2-month-more">+{summary.moreCount} more</span>}
          </div>
        </>
      ) : null}
    </div>
  );
}

function MonthListDay({ day, summary, skeleton, onOpenDay, onOpenItem }) {
  const m = moment(day.key);
  const cls = [
    'dv2-month-row',
    day.isToday && 'dv2-month-row--today',
    summary && summary.isOff && 'dv2-month-row--off',
    summary && summary.isHoliday && 'dv2-month-row--holiday',
  ].filter(Boolean).join(' ');
  const empty = summary && !summary.allItems.length;
  return (
    <div className={cls} onClick={() => onOpenDay(day.key)} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpenDay(day.key); }}>
      <div className="dv2-month-row-date">
        <span className="dv2-month-row-dow">{m.format('ddd')}</span>
        <span className="dv2-month-row-num">{day.dayNum}</span>
      </div>
      <div className="dv2-month-row-body">
        {skeleton ? (
          <div className="dv2-month-skel"><span className="dv2-month-skel-line" /></div>
        ) : summary ? (
          <>
            <div className="dv2-month-row-meta">
              {summary.isHoliday && <span className="dv2-month-leave">{summary.holidayLabel}</span>}
              {summary.isOff && <span className="dv2-month-off">Off</span>}
              {summary.utilisation != null && !empty && <span className="dv2-month-row-pct">{pct(summary.utilisation)}% booked</span>}
              {summary.isWorking && empty && <span className="dv2-month-row-free">No bookings</span>}
            </div>
            <UtilBar summary={summary} />
            {summary.allItems.length > 0 && (
              <div className="dv2-month-items">
                {summary.allItems.map(item => <ItemRow key={item.kind + item.id} item={item} dayKey={day.key} onOpenItem={onOpenItem} />)}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

export default React.memo(MonthView);
